import { useCallback, useEffect, useRef, useState } from "react";
import { startLearningLlmStream } from "../services/learningGraphqlSseClient";
import { AttachmentInput } from "../graphql/generated/graphql";

type StreamCompletionEvent = { chatId?: string; messageId?: string };

interface StartStreamOptions {
  chatId?: string;
  workspaceId?: string;
  attachment?: AttachmentInput;
  contentNodeId?: string;
  onChunk?: (payload: { text: string; messageId?: string; isFinalChunk?: boolean }) => void;
  onComplete?: (payload: { event?: StreamCompletionEvent; text: string }) => void;
  onObjectiveCompleted?: (payload: { objectiveIds: string[] }) => void;
  onAllObjectivesCompleted?: () => void;
  onError?: (payload: { error: Error }) => void;
}

export function useLearningLlmStream() {
  const controllerRef = useRef<AbortController | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);

  useEffect(() => {
    return () => {
      controllerRef.current?.abort();
    };
  }, []);

  const startStream = useCallback(async (question: string, options: StartStreamOptions = {}) => {
    const { onComplete, onError, onChunk, onObjectiveCompleted, onAllObjectivesCompleted, ...streamOptions } = options;

    if (!streamOptions.contentNodeId) {
      throw new Error("Missing contentNodeId for learning LLM stream.");
    }

    controllerRef.current?.abort();

    const controller = new AbortController();
    controllerRef.current = controller;

    setIsStreaming(true);

    let accumulatedText = "";
    let completionEvent: StreamCompletionEvent | undefined;

    try {
      await startLearningLlmStream(
        question,
        controller,
        {
          onChunk: (event) => {
            if (event.__typename === "LLMStreamMessageDeltaEvent") {
              const delta = event.delta;
              if (delta?.__typename === "LLMStreamTextBlock") {
                const text = delta.text ?? "";
                if (text) {
                  accumulatedText += text;
                }
                onChunk?.({
                  text,
                  messageId: event.messageId,
                  isFinalChunk: event.isFinalChunk ?? false,
                });
                if (event.isFinalChunk) {
                  setIsStreaming(false);
                }
              }
            }

            if (event.__typename === "LLMStreamMessageCompletedEvent") {
              completionEvent = {
                chatId: event.message?.chatId,
                messageId: event.message?.id,
              };
              setIsStreaming(false);
              onComplete?.({ event: completionEvent, text: accumulatedText });
            }

            if (event.__typename === "LLMStreamArtifactCreatedEvent") {
              const artifact = event.artifact as
                | {
                    type?: string;
                    data?: { isCompleted?: boolean; objectiveId?: string };
                  }
                | undefined;
              if (
                artifact?.type === "objective.progress.changed" &&
                artifact?.data?.isCompleted === true &&
                typeof artifact?.data?.objectiveId === "string"
              ) {
                onObjectiveCompleted?.({ objectiveIds: [artifact.data.objectiveId] });
              }

              if (artifact?.type === "objectives.completed.all") {
                onAllObjectivesCompleted?.();
              }
            }

            if (event.__typename === "LLMStreamErrorEvent") {
              const message = event.errorMessage || "LLM stream error";
              setIsStreaming(false);
              onError?.({ error: new Error(message) });
            }
          },
          onError: (error) => {
            setIsStreaming(false);
            onError?.({ error });
          },
          onComplete: () => {
            setIsStreaming(false);
          },
        },
        streamOptions
      );
    } catch (err) {
      if ((err as DOMException)?.name === "AbortError") return;
      setIsStreaming(false);
      throw err;
    }
  }, []);

  const cancelStream = useCallback(() => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    setIsStreaming(false);
  }, []);

  return { startStream, cancelStream, isStreaming };
}

export default useLearningLlmStream;
